import React, { useEffect, useState } from 'react';
import {
  Send,
  Loader2,
  Paperclip,
  Info,
  CheckCircle2,
  AlertTriangle,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const listaCorreos = (s) =>
  String(s || '')
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

const esCorreo = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s);

/**
 * Envío de las instrucciones al proveedor de la cotización seleccionada.
 *
 * Al terminar no se cierra solo: muestra lo que pasó de verdad. El
 * backend distingue el envío real (inicia la operación) del dry-run (no
 * la inicia), y el operador tiene que ver cuál de los dos fue.
 */
export default function NotificarProveedorDialog({
  open,
  onOpenChange,
  contexto,
  onEnviar,
}) {
  const [para, setPara] = useState('');
  const [cc, setCc] = useState('');
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Se prellena sólo al ABRIR. Depender también de `contexto` borraría el
  // resultado apenas se muestra: tras enviar, la página recarga el
  // contexto (cambian el estado y la bitácora).
  useEffect(() => {
    if (!open || !contexto) return;
    setPara((contexto.destinatariosSugeridos || []).join(', '));
    setCc(contexto.solicitud?.asesorSnapshot?.correo || '');
    setAsunto('');
    setMensaje('');
    setResultado(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const destinatarios = listaCorreos(para);
  const copias = listaCorreos(cc);
  const invalidos = [...destinatarios, ...copias].filter((x) => !esCorreo(x));
  const puedeEnviar = destinatarios.length > 0 && invalidos.length === 0 && !enviando;

  const proveedor = contexto?.proveedor;
  const codigo = contexto?.solicitud?.codigo;
  const yaEnCurso = contexto?.solicitud?.estado === 'Enviada';

  const enviar = async () => {
    setEnviando(true);
    try {
      const r = await onEnviar({
        para: destinatarios,
        cc: copias,
        asunto: asunto.trim() || undefined,
        mensaje: mensaje.trim() || undefined,
      });
      setResultado(r);
    } finally {
      setEnviando(false);
    }
  };

  const ok = resultado?.success;
  const real = resultado?.data?.envio?.modo === 'smtp';
  const inicio = resultado?.data?.operacionIniciada;

  return (
    <Dialog open={open} onOpenChange={(v) => !enviando && onOpenChange?.(v)}>
      <DialogContent hideClose className="max-w-lg gap-0 p-0">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <DialogTitle className="text-lg">Notificar al proveedor</DialogTitle>
            <DialogDescription className="text-sm">
              {codigo} · {proveedor?.nombre || 'proveedor de la cotización seleccionada'}
            </DialogDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            disabled={enviando}
            onClick={() => onOpenChange?.(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {resultado ? (
          <div className="space-y-4 p-5">
            <div
              className={
                !ok
                  ? 'flex gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-800'
                  : real
                    ? 'flex gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800'
                    : 'flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900'
              }
            >
              {ok && real ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              ) : (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              )}
              <div className="space-y-1 text-sm">
                <p className="font-medium">
                  {!ok
                    ? 'No se pudo notificar'
                    : inicio
                      ? 'Operación iniciada'
                      : real
                        ? 'Proveedor notificado'
                        : 'No se envió (modo de prueba)'}
                </p>
                <p>{[].concat(resultado?.message || '').join(' · ')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={() => onOpenChange?.(false)}>
                {ok ? 'Listo' : 'Volver'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3 p-4">
              {proveedor && !proveedor.enCatalogo && (
                <p className="flex items-start gap-1.5 rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {contexto?.avisoProveedor}
                </p>
              )}
              <div className="space-y-1">
                <Label htmlFor="notif-para" className="text-xs text-muted-foreground">
                  Para
                </Label>
                <Input
                  id="notif-para"
                  value={para}
                  placeholder="operaciones@proveedor.com"
                  onChange={(e) => setPara(e.target.value)}
                />
                {proveedor?.contactos?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {proveedor.contactos
                      .filter((c) => c.correo && !destinatarios.includes(c.correo))
                      .map((c) => (
                        <button
                          key={c.correo}
                          type="button"
                          className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
                          title={[c.nombre, c.cargo, c.tipoContacto].filter(Boolean).join(' · ')}
                          onClick={() => setPara([...destinatarios, c.correo].join(', '))}
                        >
                          + {c.nombre || c.correo}
                          {c.tipoContacto ? ` (${c.tipoContacto})` : ''}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="notif-cc" className="text-xs text-muted-foreground">
                  Copia (opcional)
                </Label>
                <Input id="notif-cc" value={cc} onChange={(e) => setCc(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="notif-asunto" className="text-xs text-muted-foreground">
                  Asunto (opcional)
                </Label>
                <Input
                  id="notif-asunto"
                  value={asunto}
                  placeholder={`Instrucciones de embarque ${codigo || ''}`}
                  onChange={(e) => setAsunto(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="notif-msg" className="text-xs text-muted-foreground">
                  Mensaje (opcional)
                </Label>
                <Textarea
                  id="notif-msg"
                  rows={3}
                  value={mensaje}
                  placeholder="Si lo dejas vacío se usa un texto por defecto con el resumen del embarque."
                  onChange={(e) => setMensaje(e.target.value)}
                />
              </div>

              {invalidos.length > 0 && (
                <p className="text-xs text-rose-700">Correo mal escrito: {invalidos.join(', ')}</p>
              )}

              <p className="flex items-center gap-1.5 rounded-md bg-muted/60 p-2 text-xs text-muted-foreground">
                <Paperclip className="h-3 w-3" />
                Se adjuntan las instrucciones en PDF, con lo que tienes en pantalla.
              </p>
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                {yaEnCurso
                  ? 'La operación ya está en curso: se reenvían las instrucciones sin cambiar su estado.'
                  : 'Al enviarse el correo, la solicitud pasa a Operaciones en curso.'}
              </p>
            </div>

            <div className="flex justify-end gap-2 border-t p-4">
              <Button variant="outline" disabled={enviando} onClick={() => onOpenChange?.(false)}>
                Cancelar
              </Button>
              <Button onClick={enviar} disabled={!puedeEnviar}>
                {enviando ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Enviar
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
